/**
 * Global file for all routes
 * Migrated to shadcn/ui - December 2025
 */

import React, { lazy, Suspense } from 'react';
import ProtectedRoute from './privateRoute';
import AppShell from '../components/layout/AppShell';

// Dashboard pages (lazy loaded)
const HomeNew = lazy(() => import('../pages/Home/HomeNew'));
const Orders = lazy(() => import('../pages/Orders/Orders'));
const Settings = lazy(() => import('../pages/Settings/Settings'));
const Customers = lazy(() => import('../pages/Customers/Customers'));
const PayoutBalance = lazy(() => import('../pages/PayoutBalance/PayoutBalance'));
const CustomerOrders = lazy(() => import('../pages/Orders/CustomerOrders/CustomerOrders'));
const PayoutDetails = lazy(() => import('../pages/PayoutBalance/PayoutDetails/PayoutDetails'));
const CustomersTrack = lazy(() => import('../pages/Customers/CustomersTrack/CustomersTrack'));
const MerchantTrans = lazy(() => import('../pages/Reports/MerchantTrans'));
const StoreReport = lazy(() => import('../pages/Reports/StoreReport'));
const Accounts = lazy(() => import('../pages/Settings/Accounts'));
const ApiKeys = lazy(() => import('../pages/Settings/ApiKeys'));
const UserPassword = lazy(() => import('../pages/Settings/UserPassword'));
const PublicInfo = lazy(() => import('../pages/Settings/PublicInfo'));
const Branding = lazy(() => import('../pages/Settings/Branding'));
const WebHooks = lazy(() => import('../pages/Settings/WebHooks'));
const BankAccount = lazy(() => import('../pages/Settings/BankAccount'));
const Management = lazy(() => import('../pages/Settings/Management'));
const BussinessInfo = lazy(() => import('../pages/Settings/BussinessInfo'));
const Installation = lazy(() => import('../pages/OnsiteMessaging/Installation'));
const Placements = lazy(() => import('../pages/OnsiteMessaging/Placements'));
const StoreCreditRewards = lazy(() => import('../pages/Settings/StoreCreditRewards'));
const BeneficialOwner = lazy(() => import('../pages/Settings/BeneficialOwner'));
const Directors = lazy(() => import('../pages/Settings/Directors'));
const CampaignList = lazy(() => import('../pages/Campaign/CampaignList'));
const CampaignForm = lazy(() => import('../pages/Campaign/CampaignForm'));
const OfferList = lazy(() => import('../pages/Offer/OfferList'));
const OfferForm = lazy(() => import('../pages/Offer/OfferForm'));
const ManualRefunds = lazy(() => import('../pages/ManualRefunds/ManualRefunds'));

// Auth & public pages (not lazy - needed immediately)
import UserSignup from '../pages/UserSignup/UserSignup';
import SetupLoading from '../pages/LoadingPage/SetupLoading';
import ConfirmOtp from '../components/ConfirmOtp/ConfirmOtp';
import MerchantLogin from '../pages/Merchant Login/MerchantLogin';
import Spinner from '../components/Loader/Spinner';

export const appRoutes = [
  {
    // ALL dashboard routes now use shadcn AppShell - single canonical layout
    element: <AppShell />,
    children: [
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: '/home',
            element: (
              <Suspense fallback={<Spinner />}>
                <HomeNew />
              </Suspense>
            ),
          },
          {
            path: '/orders',
            element: (
              <Suspense fallback={<Spinner />}>
                <Orders />
              </Suspense>
            ),
          },
          {
            path: '/orders/:id',
            element: (
              <Suspense fallback={<Spinner />}>
                <CustomerOrders />
              </Suspense>
            ),
          },
          {
            path: '/payouts',
            element: (
              <Suspense fallback={<Spinner />}>
                <PayoutBalance />
              </Suspense>
            ),
          },
          {
            path: '/payouts/:id',
            element: (
              <Suspense fallback={<Spinner />}>
                <PayoutDetails />
              </Suspense>
            ),
          },
          {
            path: '/transaction-report',
            element: (
              <Suspense fallback={<Spinner />}>
                <MerchantTrans type='merchant' />
              </Suspense>
            ),
          },
          {
            path: '/payout-report',
            element: (
              <Suspense fallback={<Spinner />}>
                <MerchantTrans type='balance' />
              </Suspense>
            ),
          },
          {
            path: '/store-credit-report',
            element: (
              <Suspense fallback={<Spinner />}>
                <StoreReport />
              </Suspense>
            ),
          },
          {
            path: '/customers',
            element: (
              <Suspense fallback={<Spinner />}>
                <Customers />
              </Suspense>
            ),
          },
          {
            path: '/customers/:id',
            element: (
              <Suspense fallback={<Spinner />}>
                <CustomersTrack />
              </Suspense>
            ),
          },
          {
            path: '/manual-refunds',
            element: (
              <Suspense fallback={<Spinner />}>
                <ManualRefunds />
              </Suspense>
            ),
          },
          {
            path: '/settings',
            element: (
              <Suspense fallback={<Spinner />}>
                <Settings />
              </Suspense>
            ),
          },
          {
            path: '/settings/account',
            element: (
              <Suspense fallback={<Spinner />}>
                <Accounts />
              </Suspense>
            ),
          },
          {
            path: '/settings/user',
            element: (
              <Suspense fallback={<Spinner />}>
                <UserPassword />
              </Suspense>
            ),
          },
          {
            path: '/settings/public',
            element: (
              <Suspense fallback={<Spinner />}>
                <PublicInfo />
              </Suspense>
            ),
          },
          {
            path: '/settings/branding',
            element: (
              <Suspense fallback={<Spinner />}>
                <Branding />
              </Suspense>
            ),
          },
          {
            path: '/settings/business',
            element: (
              <Suspense fallback={<Spinner />}>
                <BussinessInfo />
              </Suspense>
            ),
          },
          {
            path: '/settings/management',
            element: (
              <Suspense fallback={<Spinner />}>
                <Management />
              </Suspense>
            ),
          },
          {
            path: '/settings/beneficial',
            element: (
              <Suspense fallback={<Spinner />}>
                <BeneficialOwner />
              </Suspense>
            ),
          },
          {
            path: '/settings/business-excecutive',
            element: (
              <Suspense fallback={<Spinner />}>
                <Directors name='business-excecutive' />
              </Suspense>
            ),
          },
          {
            path: '/settings/payout-account',
            element: (
              <Suspense fallback={<Spinner />}>
                <BankAccount />
              </Suspense>
            ),
          },
          {
            path: '/settings/store-credit-rewards',
            element: (
              <Suspense fallback={<Spinner />}>
                <StoreCreditRewards />
              </Suspense>
            ),
          },
          {
            path: '/developer',
            element: (
              <Suspense fallback={<Spinner />}>
                <Placements />
              </Suspense>
            ),
          },
          {
            path: '/campaigns',
            element: (
              <Suspense fallback={<Spinner />}>
                <CampaignList />
              </Suspense>
            ),
          },
          {
            path: '/add-campaign',
            element: (
              <Suspense fallback={<Spinner />}>
                <CampaignForm />
              </Suspense>
            ),
          },
          {
            path: '/update-campaign/:id',
            element: (
              <Suspense fallback={<Spinner />}>
                <CampaignForm />
              </Suspense>
            ),
          },
          {
            path: '/offers',
            element: (
              <Suspense fallback={<Spinner />}>
                <OfferList />
              </Suspense>
            ),
          },
          {
            path: '/offers/:id/:uniqueId',
            element: (
              <Suspense fallback={<Spinner />}>
                <OfferList />
              </Suspense>
            ),
          },
          {
            path: '/add-offer',
            element: (
              <Suspense fallback={<Spinner />}>
                <OfferForm />
              </Suspense>
            ),
          },
          {
            path: '/update-offer/:id',
            element: (
              <Suspense fallback={<Spinner />}>
                <OfferForm />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    // Public routes (no auth required)
    children: [
      {
        path: '/signup/:id',
        element: <UserSignup />,
      },
      {
        path: '/loading',
        element: <SetupLoading />,
      },
      {
        path: '',
        element: <MerchantLogin />,
      },
      {
        path: '/signup/confirm',
        element: <ConfirmOtp />,
      },
    ],
  },
];
